import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  surface: {
    width: "100%",
    border: "1px solid var(--total_row_boldbackground)",
    borderRadius: "calc(16px * var(--app-radius-scale, 1))",
    backgroundColor: "var(--color-background-paper)",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
    overflow: "hidden",
    'html[data-mode="dark"] &': {
      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.4)",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2, 1.25),
    backgroundColor: "var(--color-background-paper)",
    borderBottom: "1px solid var(--total_row_boldbackground)",
  },
  headerText: {
    minWidth: 0,
    flex: "1 1 240px",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      "& > *": { flex: "1 1 auto" },
    },
  },
  content: {
    padding: theme.spacing(1.5, 2, 2),
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1.25, 1.25, 1.5),
    },
  },
  title: {
    fontSize: "large",
    fontWeight: 500,
    lineHeight: 1.6,
    letterSpacing: "0.0075em",
    color: "var(--color-text-primary)",
    margin: 0,
  },
  subtle: {
    color: "var(--color-text-secondary)",
    fontSize: "0.8125rem",
  },
  hint: {
    color: "var(--color-text-secondary)",
    fontSize: "0.75rem",
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(0.75),
    lineHeight: 1.5,
  },
  hintSpaced: {
    marginTop: theme.spacing(1),
  },
  hintIcon: {
    fontSize: "0.9375rem",
    color: "var(--color-primary-text)",
    flexShrink: 0,
    marginTop: 2,
  },

  tabsBar: {
    borderBottom: "1px solid var(--total_row_boldbackground)",
    marginBottom: theme.spacing(1.5),
  },
  tabsRoot: {
    minHeight: 42,
    "& .MuiTabs-indicator": {
      height: 3,
      borderRadius:
        "calc(3px * var(--app-radius-scale, 1)) calc(3px * var(--app-radius-scale, 1)) 0 0",
      backgroundColor: "var(--color-primary-main)",
    },
    "& .MuiTabs-scrollButtons": {
      color: "var(--color-text-secondary)",
    },
  },
  tabRoot: {
    textTransform: "none",
    fontSize: "0.875rem",
    fontWeight: 500,
    minHeight: 42,
    minWidth: 90,
    padding: theme.spacing(0, 1.5),
    color: "var(--color-text-secondary)",
    "& .MuiTab-wrapper": {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      whiteSpace: "nowrap",
    },
    "&.Mui-selected": {
      color: "var(--color-primary-text)",
      fontWeight: 700,
    },
    "&:hover": {
      color: "var(--color-primary-text)",
      opacity: 1,
    },
  },
  tabCount: {
    fontSize: "0.6875rem",
    fontWeight: 700,
    lineHeight: 1.5,
    minWidth: 18,
    padding: "0 5px",
    borderRadius: "calc(9px * var(--app-radius-scale, 1))",
    color: "var(--color-text-secondary)",
    backgroundColor: "var(--total_row_background)",
    ".Mui-selected &": {
      color: "var(--color-primary-text)",
      backgroundColor:
        "color-mix(in srgb, var(--color-primary-main) 14%, transparent)",
    },
  },

  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.25),
    flexWrap: "wrap",
    marginBottom: theme.spacing(0.75),
  },
  filtersRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    minWidth: 0,
  },
  searchBlock: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 2,
    marginLeft: "auto",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      marginLeft: 0,
      alignItems: "stretch",
    },
  },
  toolbarRight: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  searchInput: {
    width: 240,
    margin: 0,
    "& .MuiOutlinedInput-root": {
      minHeight: 36,
      borderRadius: "calc(8px * var(--app-radius-scale, 1))",
      backgroundColor: "var(--color-background-default)",
      fontSize: "0.8125rem",
      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
      "& fieldset": { borderColor: "var(--total_row_boldbackground)" },
      "&:hover fieldset": { borderColor: "var(--color-primary-main)" },
      "&.Mui-focused fieldset": {
        borderColor: "var(--color-primary-main)",
        borderWidth: 1.5,
      },
    },
    "& .MuiOutlinedInput-adornedStart": { paddingLeft: 10 },
    "& .MuiInputAdornment-positionStart": { marginRight: 6 },
    "& .MuiOutlinedInput-input": {
      padding: "8px 10px 8px 0",
      color: "var(--color-text-primary)",
    },
    "& .MuiInputLabel-outlined": {
      fontSize: "0.8125rem",
      color: "var(--color-text-secondary)",
      "&.MuiInputLabel-shrink": {
        transform: "translate(14px, -6px) scale(0.75)",
      },
    },
    [theme.breakpoints.down("xs")]: { width: "100%" },
  },
  resultCount: {
    color: "var(--color-text-secondary)",
    fontSize: "0.6875rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
    paddingRight: 2,
  },
  chipsRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    flexWrap: "wrap",
    "& .MuiChip-root": {
      height: "auto",
      minHeight: 26,
      padding: "2px 0",
      fontSize: "0.75rem",
      fontWeight: 600,
      borderRadius: "calc(20px * var(--app-radius-scale, 1))",
      transition: "background-color 0.2s ease, border-color 0.2s ease",
    },
    "& .MuiChip-outlined": {
      color: "var(--color-text-secondary)",
      borderColor: "var(--total_row_boldbackground)",
      backgroundColor: "var(--color-background-paper)",
      "&:hover": {
        borderColor: "var(--color-primary-main)",
        color: "var(--color-primary-text)",
        backgroundColor: "var(--total_row_background)",
      },
    },
    "& .MuiChip-colorPrimary": {
      backgroundColor: "var(--color-primary-main)",
      color: "var(--color-primary-contrast)",
      "&:hover": { filter: "brightness(1.08)" },
    },
  },

  tableContainer: {
    width: "100%",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    border: "1px solid var(--total_row_boldbackground)",
    borderRadius: "calc(10px * var(--app-radius-scale, 1))",
    backgroundColor: "var(--color-background-paper)",
  },
  tableSpaced: {
    marginTop: theme.spacing(0.75),
  },
  table: {
    width: "100%",
  },
  tableWide: {
    minWidth: 720,
  },
  tableHead: {
    "& th": {
      backgroundColor: "var(--chart_header_background) !important",
      color: "var(--color-primary-contrast) !important",
      fontWeight: 700,
      fontSize: "0.8125rem",
      padding: theme.spacing(0.75, 1.25),
      borderBottom: "none",
      whiteSpace: "nowrap",
      userSelect: "none",
    },
  },
  tableBody: {
    "& td": {
      fontSize: "0.8125rem",
      color: "var(--color-text-primary)",
      padding: theme.spacing(0.5, 1.25),
      lineHeight: 1.4,
      borderBottom: "1px solid var(--total_row_boldbackground)",
    },
    "& tr:last-child td": {
      borderBottom: "none",
    },
    "& tr": {
      transition: "background-color 0.15s ease",
      "&:hover": {
        backgroundColor: "var(--total_row_background)",
      },
    },
  },
  clickableRow: {
    cursor: "pointer",
  },
  sortLabel: {
    color: "var(--color-primary-contrast) !important",
    "&:hover": {
      color: "var(--color-primary-contrast) !important",
      opacity: 0.9,
    },
    "& .MuiTableSortLabel-icon": {
      color: "var(--color-primary-contrast) !important",
      opacity: 0.7,
    },
  },
  cellStrong: {
    fontWeight: 600,
    lineHeight: 1.35,
    color: "var(--color-text-primary)",
  },
  cellNowrap: {
    whiteSpace: "nowrap",
  },
  cellActions: {
    whiteSpace: "nowrap",
    textAlign: "right",
  },

  emptyState: {
    width: "100%",
    minHeight: 120,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3),
    textAlign: "center",
    color: "var(--color-text-secondary)",
    fontSize: "0.875rem",
    fontWeight: 500,
    letterSpacing: "0.025em",
  },
  emptyStateActions: {
    marginTop: theme.spacing(1),
  },

  backButton: {
    minWidth: 0,
    minHeight: 0,
    padding: theme.spacing(0.25, 0.75, 0.25, 0.25),
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(-0.25),
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "none",
    color: "var(--color-primary-text)",
    background: "none",
    boxShadow: "none",
    "& .MuiButton-startIcon": { marginRight: 2, marginLeft: 0 },
    "& .MuiSvgIcon-root": { fontSize: "1rem" },
    "&:hover": {
      background: "none",
      boxShadow: "none",
      textDecoration: "underline",
    },
  },
  backButtonInline: {
    marginBottom: 0,
  },
  subViewHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  recordTitle: {
    fontSize: "0.9375rem",
    fontWeight: 700,
    color: "var(--color-text-primary)",
    margin: 0,
  },

  formShell: {
    display: "flex",
    flexDirection: "column",
    maxHeight: "calc(100vh - 250px)",
    minHeight: 340,
  },
  formShellAuto: {
    minHeight: 0,
  },
  formTopBar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    flexShrink: 0,
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(1),
    border: "1px solid var(--total_row_boldbackground)",
    borderRadius: "calc(10px * var(--app-radius-scale, 1))",
    background:
      "linear-gradient(90deg, color-mix(in srgb, var(--color-primary-main) 8%, var(--color-background-paper)) 0%, var(--color-background-paper) 55%)",
  },
  formBackButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 30,
    height: 30,
    flexShrink: 0,
    padding: 0,
    border: "1px solid var(--total_row_boldbackground)",
    borderRadius: "calc(8px * var(--app-radius-scale, 1))",
    background: "var(--color-background-paper)",
    color: "var(--color-primary-text)",
    cursor: "pointer",
    transition: "border-color 0.2s ease, background-color 0.2s ease",
    "&:hover": {
      borderColor: "var(--color-primary-main)",
      backgroundColor:
        "color-mix(in srgb, var(--color-primary-main) 10%, transparent)",
    },
    "&:focus-visible": {
      outline: "2px solid var(--color-primary-main)",
      outlineOffset: 1,
    },
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
    "& .MuiSvgIcon-root": { fontSize: "1.125rem" },
  },
  formTitle: {
    fontSize: "1rem",
    fontWeight: 700,
    lineHeight: 1.3,
    color: "var(--color-text-primary)",
    margin: 0,
  },
  formTopBarActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginLeft: "auto",
    flexWrap: "wrap",
  },
  formBody: {
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    padding: theme.spacing(0, 0.5, 0, 0),
  },
  formActionBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexShrink: 0,
    paddingTop: theme.spacing(1.25),
    marginTop: theme.spacing(0.5),
    borderTop: "1px solid var(--total_row_boldbackground)",
    backgroundColor: "var(--color-background-paper)",
    [theme.breakpoints.down("xs")]: {
      "& > *": { flex: "1 1 auto" },
    },
  },
  addRowButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    textTransform: "none",
    letterSpacing: "normal",
    padding: theme.spacing(0.5, 1.25),
    marginBottom: theme.spacing(1),
    border: "1px dashed var(--color-primary-main)",
    borderRadius: "calc(6px * var(--app-radius-scale, 1))",
    background: "none",
    font: "inherit",
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "var(--color-primary-text)",
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "background-color 0.2s ease",
    "&:hover": { backgroundColor: "var(--total_row_background)" },
    "&:focus-visible": {
      outline: "2px solid var(--color-primary-main)",
      outlineOffset: 1,
    },
    "& .MuiSvgIcon-root": { fontSize: "0.9375rem" },
  },

  sectionHeading: {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "var(--color-primary-text)",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    margin: theme.spacing(1.5, 0, 0.75),
    "&::before": {
      content: "''",
      display: "inline-block",
      width: 3,
      height: 14,
      flexShrink: 0,
      backgroundColor: "var(--color-primary-main)",
      borderRadius: "calc(2px * var(--app-radius-scale, 1))",
    },
  },
  sectionHeadingFirst: {
    marginTop: theme.spacing(0.5),
  },
  headingAction: {
    marginLeft: "auto",
    marginBottom: 0,
  },
  sectionCard: {
    border: "1px solid var(--total_row_boldbackground)",
    borderRadius: "calc(10px * var(--app-radius-scale, 1))",
    padding: theme.spacing(1.25, 1.5),
    [theme.breakpoints.down("xs")]: { padding: theme.spacing(1) },
    backgroundColor: "var(--color-background-paper)",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
    'html[data-mode="dark"] &': {
      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.4)",
    },
  },

  rowItem: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1.25),
    padding: theme.spacing(1, 1.5),
    border: "1px solid var(--total_row_boldbackground)",
    borderRadius: "calc(10px * var(--app-radius-scale, 1))",
    backgroundColor: "var(--color-background-paper)",
    marginBottom: theme.spacing(0.75),
    fontSize: "0.8125rem",
    transition: "border-color 0.2s ease, background-color 0.2s ease",
    "&:hover": {
      borderColor: "var(--color-primary-main)",
      backgroundColor: "var(--total_row_background)",
    },
  },
  rowItemStatic: {
    "&:hover": {
      borderColor: "var(--total_row_boldbackground)",
      backgroundColor: "var(--color-background-paper)",
    },
  },
  rowItemDeleted: {
    backgroundColor: "var(--total_row_background)",
    "&:hover": {
      borderColor: "var(--total_row_boldbackground)",
      backgroundColor: "var(--total_row_background)",
    },
  },
  rowGrow: {
    flex: "1 1 180px",
    minWidth: 0,
  },
  // The preview sits in a narrow right-hand column, so the meta cluster wraps under the loan
  // name rather than squeezing every word onto its own line.
  rowMetaGroup: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  rowMeta: {
    color: "var(--color-text-secondary)",
    fontSize: "0.75rem",
    whiteSpace: "nowrap",
    textAlign: "right",
  },
  cellMeta: {
    color: "var(--color-text-secondary)",
    fontSize: "0.6875rem",
    lineHeight: 1.35,
  },
  avatar: {
    width: 32,
    height: 32,
    fontSize: "0.75rem",
    fontWeight: 700,
    flexShrink: 0,
    color: "var(--color-primary-contrast)",
    backgroundColor: "var(--color-primary-main)",
  },
  strikethrough: {
    textDecoration: "line-through",
    fontWeight: 600,
    color: "var(--color-text-secondary)",
  },

  linkAction: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: theme.spacing(0.25, 0.75),
    border: "none",
    background: "none",
    font: "inherit",
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "var(--color-primary-text)",
    cursor: "pointer",
    borderRadius: "calc(6px * var(--app-radius-scale, 1))",
    whiteSpace: "nowrap",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "var(--total_row_background)",
      textDecoration: "underline",
    },
    "&:focus-visible": {
      outline: "2px solid var(--color-primary-main)",
      outlineOffset: 1,
    },
    "&:disabled": {
      color: "var(--color-text-secondary)",
      cursor: "not-allowed",
      opacity: 0.6,
    },
  },
  rowLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontWeight: 600,
    color: "var(--color-primary-text)",
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  },
  rowLinkIcon: {
    fontSize: "0.8125rem",
    opacity: 0.75,
  },
  iconAction: {
    padding: 6,
    color: "var(--color-primary-text) !important",
    "&:hover": { backgroundColor: "rgba(128, 128, 128, 0.1)" },
  },
  iconActionDanger: {
    padding: 6,
    color: "var(--status-error-color) !important",
    "&:hover": {
      backgroundColor:
        "color-mix(in srgb, var(--status-error-color) 10%, transparent)",
    },
  },
  dangerButton: {
    backgroundColor: "var(--color-background-paper) !important",
    color: "var(--status-error-color) !important",
    borderColor:
      "color-mix(in srgb, var(--status-error-color) 45%, transparent) !important",
    boxShadow: "none !important",
    "&:hover": {
      backgroundColor:
        "color-mix(in srgb, var(--status-error-color) 8%, transparent) !important",
      borderColor: "var(--status-error-color) !important",
      filter: "none",
    },
  },

  statusTag: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 9px",
    borderRadius: "calc(20px * var(--app-radius-scale, 1))",
    fontSize: "0.75rem",
    fontWeight: 700,
    letterSpacing: "0.02em",
    whiteSpace: "nowrap",
  },
  tagValid: {
    backgroundColor:
      "color-mix(in srgb, var(--status-success-color) 15%, transparent)",
    color: "var(--status-success-color)",
  },
  tagExpiring: {
    backgroundColor: "color-mix(in srgb, var(--heat-slight) 18%, transparent)",
    color: "var(--heat-slight)",
  },
  tagNeutral: {
    backgroundColor: "var(--total_row_background)",
    color: "var(--color-text-secondary)",
  },
  tagDanger: {
    backgroundColor:
      "color-mix(in srgb, var(--status-error-color) 15%, transparent)",
    color: "var(--status-error-color)",
  },
  countChip: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: "0.6875rem",
    fontWeight: 700,
    padding: "1px 9px",
    borderRadius: "calc(20px * var(--app-radius-scale, 1))",
    color: "var(--color-primary-text)",
    backgroundColor:
      "color-mix(in srgb, var(--color-primary-main) 14%, transparent)",
  },

  validationSummary: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    color: "var(--status-error-color)",
    fontSize: "0.8125rem",
    lineHeight: 1.5,
    backgroundColor:
      "color-mix(in srgb, var(--status-error-color) 8%, transparent)",
    border:
      "1px solid color-mix(in srgb, var(--status-error-color) 45%, transparent)",
    borderRadius: "calc(10px * var(--app-radius-scale, 1))",
    padding: theme.spacing(1.25, 1.5),
    marginBottom: theme.spacing(1.5),
  },
  validationIcon: {
    fontSize: "1rem",
    flexShrink: 0,
    marginTop: 2,
  },

  field: {
    width: "100%",
    margin: 0,
    verticalAlign: "top",
    "& .MuiOutlinedInput-root": {
      minHeight: 34,
      borderRadius: "calc(8px * var(--app-radius-scale, 1))",
      fontSize: "0.8125rem",
      backgroundColor: "var(--color-background-default)",
      "& fieldset": { borderColor: "var(--total_row_boldbackground)" },
      "&:hover fieldset": { borderColor: "var(--color-primary-main)" },
      "&.Mui-focused fieldset": {
        borderColor: "var(--color-primary-main)",
        borderWidth: 1.5,
      },
    },
    "& .MuiOutlinedInput-multiline": { minHeight: 0 },
    "& .MuiInputLabel-outlined": {
      fontSize: "0.8125rem",
      color: "var(--color-text-secondary)",
      transform: "translate(12px, 9px) scale(1)",
      "&.MuiInputLabel-shrink": {
        transform: "translate(12px, -6px) scale(0.75)",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "7px 12px",
      color: "var(--color-text-primary)",
    },
    "& .MuiOutlinedInput-inputMultiline": { resize: "vertical", padding: 0 },
    "& .MuiOutlinedInput-adornedEnd": { paddingRight: 6 },

    "& .MuiSelect-outlined.MuiSelect-outlined": {
      padding: "7px 30px 7px 12px",
    },
    "& .MuiInputAdornment-positionStart svg": {
      fontSize: "1rem",
      color: "var(--color-text-secondary)",
    },
    "& .MuiFormHelperText-root": {
      fontSize: "0.6875rem",
      marginLeft: 2,
      marginTop: 2,
      color: "var(--color-text-secondary)",
      "&.Mui-error": { color: "var(--status-error-color)" },
    },
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: theme.spacing(1.25),
    alignItems: "start",
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  editorRow: {
    border: "1px solid var(--total_row_boldbackground)",
    borderRadius: "calc(10px * var(--app-radius-scale, 1))",
    padding: theme.spacing(1.25),
    [theme.breakpoints.down("xs")]: { padding: theme.spacing(1) },
    marginBottom: theme.spacing(0.75),
    backgroundColor: "var(--color-background-paper)",
  },
  editorRowHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.75),
  },
  editorRowLabel: {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: "var(--color-text-secondary)",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  editorRowActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    "& .MuiFormControlLabel-root": {
      margin: 0,
      height: 28,
      display: "inline-flex",
      alignItems: "center",
    },
    "& .MuiFormControlLabel-label": {
      fontSize: "0.75rem",
      lineHeight: 1,
      color: "var(--color-text-primary)",
    },
    "& .MuiSwitch-root": { margin: 0 },
    "& .MuiIconButton-root": {
      width: 28,
      height: 28,
      padding: 0,
    },
  },
  editorGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: theme.spacing(1.25),
    alignItems: "start",
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  grid2: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.5fr) minmax(0, 1fr)",
    gap: theme.spacing(3),
    alignItems: "start",
    [theme.breakpoints.down("md")]: { gridTemplateColumns: "1fr" },
  },
  detailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: theme.spacing(0.875, 1.5),
    alignItems: "start",
  },
  detailRow: {
    marginBottom: theme.spacing(0.875),
    minWidth: 0,
  },
  detailRowWide: {
    gridColumn: "1 / -1",
  },
  detailRowFlush: {
    marginBottom: 0,
  },
  detailLabel: {
    fontSize: "0.625rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    lineHeight: 1.4,
    color: "var(--color-text-secondary)",
    marginBottom: 0,
  },
  detailValue: {
    fontSize: "0.8125rem",
    lineHeight: 1.4,
    color: "var(--color-text-primary)",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
  },
  flagsRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(0.25),
    borderTop: "1px solid var(--total_row_boldbackground)",
    marginTop: theme.spacing(0.75),
    paddingTop: theme.spacing(0.75),
  },
  toggleWrap: {
    display: "flex",
    alignItems: "center",
    "& .MuiFormControlLabel-root": { margin: 0 },
    "& .MuiFormControlLabel-label": {
      fontSize: "0.75rem",
      color: "var(--color-text-primary)",
    },
  },
  formFooter: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    borderTop: "1px solid var(--total_row_boldbackground)",
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(1.5),
  },

  dropzone: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    border: "1px dashed var(--color-primary-main)",
    borderRadius: "calc(10px * var(--app-radius-scale, 1))",
    padding: theme.spacing(1.25, 2),
    width: "100%",
    maxWidth: 520,
    margin: "0 auto",
    textAlign: "center",
    background: "var(--total_row_background)",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "border-color 0.2s ease, background-color 0.2s ease",
    "&:hover": { background: "var(--total_row_boldbackground)" },
    "&:focus-visible": {
      outline: "2px solid var(--color-primary-main)",
      outlineOffset: 2,
    },
  },
  // Once a file is chosen the dashed invitation has done its job: it becomes a plain full-width
  // selected-file bar that lines up with the validation table below it.
  dropzoneFilled: {
    maxWidth: "none",
    margin: 0,
    justifyContent: "flex-start",
    textAlign: "left",
    borderStyle: "solid",
    borderColor: "var(--total_row_boldbackground)",
    background: "var(--color-background-paper)",
    "&:hover": { background: "var(--total_row_background)" },
  },
  dropzoneDragging: {
    background: "var(--total_row_boldbackground) !important",
  },
  dropzoneIcon: {
    fontSize: "1.125rem",
    color: "var(--color-primary-text)",
    pointerEvents: "none",
  },
  dropzoneMainText: {
    fontSize: "0.8125rem",
    fontWeight: 600,
    color: "var(--color-text-primary)",
    pointerEvents: "none",
  },
  dropzoneBrowse: {
    color: "var(--color-primary-text)",
    fontWeight: 700,
    textDecoration: "underline",
  },
  dropzoneHint: {
    fontSize: "0.6875rem",
    color: "var(--color-text-secondary)",
    pointerEvents: "none",
    "&::before": { content: "'·'", margin: "0 6px" },
  },
  templateRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    margin: theme.spacing(1, 0, 0),
  },
  summaryBar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.25),
    fontSize: "0.75rem",
    color: "var(--color-text-secondary)",
  },
  errorList: {
    margin: 0,
    paddingLeft: theme.spacing(2),
  },

  lockedLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  },
  lockedLabelIcon: {
    fontSize: "0.875rem",
    color: "var(--color-text-secondary)",
  },

  dialogPaper: {
    width: "100%",
  },
  dialogTitleText: {
    fontSize: "1rem",
    fontWeight: 700,
    letterSpacing: "normal",
    color: "var(--color-text-primary)",
  },
  dialogSubtitle: {
    color: "var(--color-text-secondary)",
    fontSize: "0.75rem",
    fontWeight: 600,
    margin: theme.spacing(0, 0, 0.75),
  },

  // FIRM-DOCUMENTS-033 (v1.12.0, LLD §0.11): A_SimpleDialog's own DialogContent scrolls as one
  // block by default (its `overflow-y: auto`, no prop to change it — it's a shared atom), which is
  // why a dialog's search bar used to scroll away along with its table. Applied alongside
  // `dialogPaper` (not merged into it — AssociatedLoansDialog.jsx uses `dialogPaper` too and has
  // the identical scrolling-together defect today, deliberately left unfixed here, see that task's
  // Follow-ups) on the div passed as `dialogContent`: bounding its height and making it a flex
  // column means DialogContent itself never needs to scroll — the nested `dialogScrollBody` below
  // becomes the one true scrolling region instead.
  dialogScrollShell: {
    display: "flex",
    flexDirection: "column",
    maxHeight: "calc(100vh - 260px)",
    overflow: "hidden",
    // Without this, the search field's floating label (shrunk state sits ~6px above the input's
    // own top edge, per the `.MuiInputLabel-outlined.MuiInputLabel-shrink` transform above) pokes
    // above this shell's own top boundary and gets clipped by `overflow: hidden` — the label looks
    // cut off/missing its top. This just gives it room to sit fully inside the bounded box.
    paddingTop: theme.spacing(1),
  },
  // The search block and the pagination footer — never part of the scrolling region.
  dialogScrollFixed: {
    flexShrink: 0,
  },
  // The table wrapper — the ONLY child of dialogScrollShell that scrolls.
  dialogScrollBody: {
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
  },
  // Applied alongside (not instead of) `tableHead` on <TableHead> — pins the header cells to the
  // top of `dialogScrollBody` while the rows beneath scroll. `tableHead`'s own background/color
  // rules already make each `th` opaque, which sticky positioning needs so scrolling rows don't
  // show through underneath.
  stickyTableHead: {
    "& th": {
      position: "sticky",
      top: 0,
      zIndex: 1,
    },
  },

  uploadHint: {
    color: "var(--color-text-secondary)",
    fontSize: "0.75rem",
    marginTop: theme.spacing(0.5),
  },

  // FIRM-DOCUMENTS-021 (LLD §0.5): visual reference is M_AddTaskForm.jsx's file-upload section —
  // these are new, distinctly-named classes (not a same-named override of this file's own
  // sectionCard/field) copied from M_AddTaskForm.styles.js so the two stylesheets never collide;
  // M_AddTaskForm.styles.js itself is deliberately not imported here (cross-molecule coupling).
  uploadSectionLabel: {
    fontSize: "0.6875rem",
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
    color: "var(--color-primary-text)",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    "&:first-child": {
      marginTop: 0,
    },
  },
  uploadSectionCard: {
    padding: 0,
    backgroundColor: "transparent",
  },
  docDropzone: {
    border: "2px dashed var(--total_row_boldbackground)",
    borderRadius: "calc(10px * var(--app-radius-scale, 1))",
    padding: theme.spacing(1.5),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "all 0.2s ease",
    backgroundColor: "var(--color-background-default)",
    minHeight: 72,
    "&:hover": {
      borderColor: "var(--color-primary-main)",
      backgroundColor: "var(--total_row_background)",
    },
    "&:focus-visible": {
      borderColor: "var(--color-primary-main)",
      outline: "2px solid var(--color-primary-main)",
      outlineOffset: 2,
    },
  },
  docDropzoneActive: {
    borderColor: "var(--color-primary-main)",
    backgroundColor: "var(--total_row_background)",
  },
  docDropzoneIcon: {
    fontSize: "1.5rem",
    color: "var(--color-text-secondary)",
    marginBottom: 2,
  },
  docDropzoneText: {
    fontSize: "0.75rem",
    color: "var(--color-text-secondary)",
    fontWeight: 500,
    textAlign: "center",
  },
  docDropzoneBrowse: {
    color: "var(--color-primary-text)",
    fontWeight: 700,
    cursor: "pointer",
  },

  documentNameCell: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  documentIcon: {
    flexShrink: 0,
  },
  truncatedName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },

  // FIRM-DUPLICATE-NAMES-007 — the Company Name field's inline duplicate warning.
  // Keys are prefixed "duplicateName" because this file is shared by FirmForm, BulkUpload and
  // the Firm Documents components, and a repeated key here silently overrides the earlier one
  // rather than erroring.
  duplicateNameCell: {
    // Keeps the field and its warning as ONE cell of the form grid, so the warning appearing
    // does not reflow the fields beside it.
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  duplicateNameWarning: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(0.75),
    // Warning tone, not error tone: advisory until Save. --heat-slight is the same amber the
    // "Expiring" status tag uses in this feature.
    color: "var(--heat-slight)",
    fontSize: "0.75rem",
    lineHeight: 1.4,
  },
  duplicateNameWarningIcon: {
    fontSize: "0.875rem",
    flexShrink: 0,
    marginTop: 1,
  },
  // FIRM-DUPLICATE-NAMES-009: "View company" in the duplicate warning. A <button> styled as a
  // link — the screen has no per-firm URL, so there is no href to give an <a>, and a bare <a>
  // without one is not keyboard-operable.
  duplicateNameViewLink: {
    background: "none",
    border: "none",
    padding: 0,
    marginLeft: theme.spacing(0.75),
    font: "inherit",
    color: "var(--color-primary-main)",
    textDecoration: "underline",
    cursor: "pointer",
    "&:hover": { textDecoration: "none" },
  },

  // FIRM-DUPLICATE-NAMES-009: the read-only existing-company dialog's body.
  existingFirmBody: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    minWidth: 0,
    padding: theme.spacing(0.5, 0),
  },
  existingFirmTags: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(0.5),
  },
  existingFirmRow: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "baseline",
    fontSize: "0.8125rem",
    lineHeight: 1.5,
  },
  existingFirmLabel: {
    flex: "0 0 150px",
    color: "var(--color-text-secondary)",
  },
  existingFirmValue: {
    flex: 1,
    minWidth: 0,
    color: "var(--color-text-primary)",
    wordBreak: "break-word",
  },
  existingFirmHint: {
    marginTop: theme.spacing(1),
    color: "var(--color-text-secondary)",
    fontSize: "0.75rem",
    lineHeight: 1.5,
  },

  // FIRM-DUPLICATE-NAMES-008 — the bulk-upload notice above Import: "n companies in this file
  // already exist…", and the degraded "couldn't check" variant. Same amber as the Expiring tag,
  // because it is advisory: nothing here blocks the import, it says what will be skipped.
  duplicateRowsNotice: {
    color: "var(--heat-slight)",
    backgroundColor: "color-mix(in srgb, var(--heat-slight) 10%, transparent)",
    border:
      "1px solid color-mix(in srgb, var(--heat-slight) 40%, transparent)",
    borderRadius: "calc(10px * var(--app-radius-scale, 1))",
    padding: theme.spacing(1, 1.5),
    marginTop: theme.spacing(1),
    fontSize: "0.8125rem",
    lineHeight: 1.5,
  },
}));

export default useStyles;
